export interface CapabilitySpec {
    label: string
    value: string
}

export interface Capability {
    id: string
    number: string
    name: string
    figure: string
    shape: string
    headline: Array<string>
    summary: [string, string]
    homeSpecs: [string, string, string]
    body: string
    detail: string
    specs: Array<CapabilitySpec>
    previewLabel: string
    caption: string
}

export const CAPABILITIES: Array<Capability> = [
    {
        id: 'capture',
        number: '01',
        name: 'Capture',
        figure: 'Lidar and photogrammetry',
        shape: 'library',
        headline: ['Measured,', 'not assumed.'],
        summary: ['Every site is scanned before we draw,', 'so the first line sits on real ground.'],
        homeSpecs: ['Scan density · 2 mm', 'Robotic total station + drone lidar', 'Turnaround · 48 h'],
        body: 'Terrestrial and airborne lidar, fused with photogrammetry, give every project a registered point cloud of what is really there before a single line is drawn.',
        detail: 'A two-person crew covers 4 000 m² of facade and interior in a day. Each scan is registered against survey control the same night, so the design team opens a checked model within 48 hours of the crew leaving site.',
        specs: [
            { label: 'Scan density', value: '2 mm at 10 m' },
            { label: 'Capture rate', value: '2 000 000 pts/s' },
            { label: 'Registration RMS', value: '1.4 mm' },
            { label: 'Equipment', value: 'Robotic total station + drone lidar' },
            { label: 'Turnaround', value: '48 h' }
        ],
        previewLabel: 'Point cloud of Hallmark Library, the timber library in Umeå, with a vertical scan plane sweeping across it.',
        caption: 'Hallmark Library / Umeå'
    },
    {
        id: 'model',
        number: '02',
        name: 'Model',
        figure: 'Thermal twin',
        shape: 'housing',
        headline: ['One model,', 'every discipline.'],
        summary: ['Architecture, structure and energy share', 'one model, rebuilt against every survey.'],
        homeSpecs: ['Federated disciplines · 11', 'Nightly rebuild · 02:00 CET', 'Clash tolerance · 5 mm'],
        body: 'Architecture, structure and energy share one federated model, rebuilt every night against the latest survey, so each decision is tested on the building as it stands.',
        detail: 'Thermal and daylight simulations run on the same geometry the contractor builds from. When the west facade of Casa Mares showed a summer peak, the fins were redrawn and the change was priced the next morning.',
        specs: [
            { label: 'Federated disciplines', value: '11' },
            { label: 'Rebuild cycle', value: 'Nightly · 02:00 CET' },
            { label: 'Clash tolerance', value: '5 mm' },
            { label: 'Simulation', value: 'Thermal · daylight · airflow' },
            { label: 'Exchange format', value: 'IFC 4.3' }
        ],
        previewLabel: 'Thermal model of Casa Mares in Lisbon, its floor slabs and facade fins coloured from cool grey to blue by heat loss.',
        caption: 'Casa Mares / Lisbon'
    },
    {
        id: 'monitor',
        number: '03',
        name: 'Monitor',
        figure: 'Occupancy model',
        shape: 'hall',
        headline: ['Buildings that', 'report back.'],
        summary: ['Heat, air, light and movement stream in,', 'checked against the design every hour.'],
        homeSpecs: ['Sensors per 1 000 m² · 38', 'Sample interval · 60 s', 'Alert latency · under 90 s'],
        body: "Sensors for heat, air, light, sound and movement stream into the model, so performance is checked against the design every hour of the building's life.",
        detail: 'At Ostra Cultural Hall, people counters showed the interval crowd stalling at one door. It was widened in the first summer shutdown and the queue fell by 64 percent.',
        specs: [
            { label: 'Sensors per 1 000 m²', value: '38' },
            { label: 'Sample interval', value: '60 s' },
            { label: 'Alert latency', value: 'Under 90 s' },
            { label: 'Channels', value: 'Heat · CO2 · lux · dB · people' },
            { label: 'Retention', value: 'Full life of the building' }
        ],
        previewLabel: 'Occupancy model of Ostra Cultural Hall in Gdańsk: a tiered auditorium, a lobby and a roof truss, with people shown as points.',
        caption: 'Ostra Cultural Hall / Gdańsk'
    },
    {
        id: 'archive',
        number: '04',
        name: 'Archive',
        figure: 'Cold storage',
        shape: 'archive',
        headline: ['Kept for', 'the next fifty years.'],
        summary: ['Scans, drawings and decisions in open formats,', 'indexed by place and checked every week.'],
        homeSpecs: ['Archive · 2.3 PB', 'Replicas · 3 sites', 'Integrity check · weekly'],
        body: 'Drawings, scans and decisions are written to open formats in three locations, indexed by place and checked for integrity every week.',
        detail: 'The archive holds 318 420 000 captured points across 38 projects in 14 countries. Any record can be traced from a sensor reading back to the scan and the decision that produced it.',
        specs: [
            { label: 'Archive size', value: '2.3 PB' },
            { label: 'Formats', value: 'IFC 4.3 · E57 · PDF/A-3' },
            { label: 'Replicas', value: '3 sites' },
            { label: 'Integrity check', value: 'Weekly · SHA-256' },
            { label: 'Retention', value: '50 years minimum' }
        ],
        previewLabel: 'The studio archive as a lattice of point columns, one for each project record, with pulsing star clusters at the peaks.',
        caption: 'Studio index / 2009 to 2026'
    }
]

export interface TimelineBand {
    id: string
    name: string
    note: string
    segments: Array<[number, number]>
}

export interface TimelineMilestone {
    month: number
    label: string
}

/**
 * The first twelve months of a typical commission, from the first survey to the first year of monitoring.
 * Segment values are month offsets from 0 to 12.
 */
export const TIMELINE_BANDS: Array<TimelineBand> = [
    { id: 'capture', name: 'Capture', note: 'Site survey, then an as-built scan', segments: [[0, 1.5], [8.5, 9.5]] },
    { id: 'model', name: 'Model', note: 'Design, simulation, construction model', segments: [[1, 8.5]] },
    { id: 'monitor', name: 'Monitor', note: 'Sensors live from handover', segments: [[9, 12]] },
    { id: 'archive', name: 'Archive', note: 'Every version kept from day one', segments: [[0, 12]] }
]

export const TIMELINE_MILESTONES: Array<TimelineMilestone> = [
    { month: 0, label: 'First scan' },
    { month: 4, label: 'Design freeze' },
    { month: 9, label: 'Handover' },
    { month: 12, label: 'Year one review' }
]
